import {
  ActionIcon,
  Box,
  Card,
  Center,
  Divider,
  Flex,
  Image,
  Progress,
  Space,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconArrowLeft, IconArrowRight, IconCopy } from "@tabler/icons-react";
import ReactECharts from "echarts-for-react";
import bgImg from "assets/images/pngs/bg.png";
import React from "react";
import HistorySaving from "./components/HistorySaving";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useDownloadSavingsContractMutation,
  useGetDetailSavingsAccountQuery,
} from "@/services/savingsAPI";
import RoutesString from "@/pages/routesString";
import ExportAccountTransactionHistory from "../accounts/components/detail-current-account-panel/components/ExportAccountTransactionHistory";
import { useDisclosure } from "@mantine/hooks";
import IconTransfer from "@/assets/images/pngs/icon-transfer.png";
import MoneyProfit from "@/assets/images/pngs/money-profit.png";
import IconDownload from "assets/images/pngs/icon-download.png";
import { notifications } from "@mantine/notifications";
import IconWallet from "@/assets/images/pngs/icon-wallet2.png";
import ImageTick from "@/assets/images/pngs/tick-double-01.png";
import { useTranslation } from "react-i18next";
import { beText } from "@/utils/beText";
import { TypeSavings } from "@/dto/savings/Savings.d";

const TermDepositDetailWrapper = () => {
  const { t } = useTranslation();

  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);

  const actions = [
    {
      key: "topup",
      text: t("TOP_UP_ACCOUNT"),
      navigate: "/savings/deposit-topup",
      type: "link",
      shows: [TypeSavings.RECURRING_DEPOSIT],
      image: IconTransfer,
    },
    {
      /**
       * #56041/#56134: sổ "rút gốc linh hoạt" thiếu hẳn lối vào màn Rút gốc từng phần nên
       * chặn cả luồng phê duyệt. Màn và route đã có sẵn (RoutesString.PartialWithdrawal),
       * chỉ thiếu mục trong danh sách thao tác.
       */
      key: "partialWithdraw",
      text: t("REMOVE_THE_ROOT_PART_BY_PART"),
      navigate: RoutesString.PartialWithdrawal,
      type: "link",
      shows: [TypeSavings.FLEXIBLE_TERM_DEPOSIT],
      image: IconWallet,
    },
    {
      key: "settlement",
      text: t("FINALIZE_DEPOSIT"),
      navigate: "/savings/deposit-settlement",
      type: "link",
      shows: [
        TypeSavings.REGULAR_TERM_DEPOSIT,
        TypeSavings.FLEXIBLE_TERM_DEPOSIT,
        TypeSavings.RECURRING_DEPOSIT,
      ],
      image: MoneyProfit,
    },
    {
      key: "download",
      text: t("DOWNLOAD_ELECTRONIC_DEPOSIT_CONTRACT"),
      navigate: "/renew",
      type: "show",
      shows: [
        TypeSavings.REGULAR_TERM_DEPOSIT,
        TypeSavings.FLEXIBLE_TERM_DEPOSIT,
        TypeSavings.RECURRING_DEPOSIT,
      ],
      image: IconDownload,
    },
  ];
  const { state } = useLocation();
  const { accountNo } = useParams();
  const [exportFileUrl, setExportFileUrl] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { data: dataDetail } = useGetDetailSavingsAccountQuery(
    { accountNo: accountNo || "" },
    { skip: !accountNo },
  );
  const [downloadSavings] = useDownloadSavingsContractMutation();
  if (!accountNo) return null;
  console.log("dataDetail", dataDetail);
  const periodicDepositMethod =
    dataDetail?.body?.accountOverview?.periodicDepositMethod;
  const accountInfo = dataDetail?.body.accountInfoFields;
  const accountNameFromApi = accountInfo?.find(
    (item) => item.key === "Tên tài khoản",
  )?.value;
  const accountNoFromApi = accountInfo?.find(
    (item) => item.key === "Số tài khoản",
  )?.value;

  /**
   * Loại sổ tiết kiệm — mã máy của savings-service (SavingsTypes):
   * REGULAR_TERM_DEPOSIT | RECURRING_DEPOSIT | FLEXIBLE_TERM_DEPOSIT.
   */
  const savingsType = dataDetail?.body?.accountOverview?.type;

  const actionFilter = actions.filter((el) => {
    // #56041/#56134: chỉ sổ rút gốc linh hoạt mới có thao tác Rút gốc từng phần.
    // Chờ có dữ liệu mới quyết — thiếu type thì ẩn, tránh mở nhầm luồng rút tiền.
    if (el.key === "partialWithdraw") {
      return savingsType === TypeSavings.FLEXIBLE_TERM_DEPOSIT;
    }
    if (periodicDepositMethod && periodicDepositMethod === "AUTO") {
      return true;
    }
    return el.key !== "topup";
  });
  const options = {
    color: ["#06578E", "#9AD9F6"],
    tooltip: {
      show: false,
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: "Account",
        type: "pie",
        radius: ["80%", "40%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 1,
        },
        label: {
          show: false,
          position: "center",
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        labelLine: {
          show: false,
        },
        data: [
          {
            value: dataDetail?.body.accountOverview?.principalAmount,
            name: t("PRINCIPAL_SUM"),
          },
          {
            value: dataDetail?.body.accountOverview?.interestAmount,
            name: t("INTEREST_SUM"),
          },
        ],
      },
    ],
  };

  const handleDownloadContract = async (account: string) => {
    try {
      const result = await downloadSavings({ accountNo: account }).unwrap();
      const fileUrl =
        result?.body?.fileUrl ||
        result?.body?.data?.fileUrl ||
        result?.body?.fileName;
      if (fileUrl) {
        setExportFileUrl(fileUrl);
        openContractInformation();
      } else {
        notifications.show({
          title: t("ERROR"),
          message: t("NO_FILE_LINK_FROM_SYSTEM"),
          color: "red",
        });
      }
    } catch (error) {
      notifications.show({
        title: t("ERROR"),
        message: t("CONTRACT_DOWNLOAD_FAILED"),
        color: "red",
      });
    }
  };

  return (
    <Box>
      <Flex align="center" gap={8} mb={15}>
        <ActionIcon
          variant="transparent"
          onClick={() => navigate(RoutesString.TermDepositList)}
        >
          <IconArrowLeft />
        </ActionIcon>
        <Title
          order={4}
          sx={{
            paddingLeft: 10,
            backgroundClip: "padding-box, border-box",
            color: "inherit",
          }}
          fz={24}
        >
          {t("DEPOSIT_ACCOUNT_DETAILS")}
        </Title>
      </Flex>
      <Flex gap={24}>
        <Box style={{ flex: 1 }}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "20px 16px",
              borderRadius: "8px",
              backgroundImage: `url(${bgImg})`,
              backgroundSize: "100% 100%",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              color: "white",
            }}
          >
            <Box
              style={{
                width: "56px",
                height: "56px",
                gap: "8px",
                borderRadius: "100px",
                padding: "14px",
                background: "#FDFDFD",
              }}
            >
              <Image src={IconWallet} />
            </Box>
            <Box>
              <Box
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                }}
              >
                {accountNameFromApi || state?.accountName}
              </Box>
              <Flex
                align="center"
                gap={4}
                style={{
                  fontSize: "14px",
                  color: "#FFFFFFCC",
                }}
              >
                STK: {accountNoFromApi || state?.accountNo}
                <IconCopy
                  size={14}
                  style={{ cursor: "pointer", opacity: 0.8 }}
                  onClick={() => {
                    const stk = accountNoFromApi || state?.accountNo;
                    if (stk) {
                      navigator.clipboard.writeText(stk);
                    }
                  }}
                />
              </Flex>
            </Box>
          </Box>
          <Space h={24} />
          <Card padding="lg" radius="md">
            <Tabs
              defaultValue="overview"
              styles={{ tabsList: { borderBottom: "none" } }}
            >
              <Flex justify="space-between">
                <Tabs.List>
                  <Tabs.Tab value="overview">{t("ACCOUNT_SUMMARY")}</Tabs.Tab>
                  <Tabs.Tab value="information">{t("ACCOUNT_INFO")}</Tabs.Tab>
                </Tabs.List>
              </Flex>

              {/* Nội dung Tab “Thẻ” */}
              <Tabs.Panel value="overview" pt="md">
                <Space h={24} />
                <Center>
                  <Flex gap={60} align="center">
                    <Box>
                      <ReactECharts
                        option={options}
                        opts={{ renderer: "canvas" }}
                        style={{ height: "300px", width: "300px" }}
                      />
                    </Box>
                    <Stack spacing={12} w={380}>
                      <Box>
                        <Text fw={600}>{t("TOTAL_BALANCE_COLON")}</Text>
                        <Text fw={600} fz={20} c="#0171BB">
                          {
                            dataDetail?.body.accountOverview
                              ?.savingsAmountDisplay
                          }
                        </Text>
                      </Box>
                      <Flex justify="space-between">
                        <Text
                          c="#6C727F"
                          sx={{
                            position: "relative",
                            paddingLeft: 24,
                            "&::before": {
                              content: '""',
                              display: "inline-block",
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: "#06578E",
                              transform: "translateY(-50%)",
                            },
                          }}
                        >
                          {t("PRINCIPAL_SUM")}
                        </Text>

                        <Text c="#121826">
                          {
                            dataDetail?.body.accountOverview
                              ?.principalAmountDisplay
                          }
                        </Text>
                      </Flex>

                      <Flex justify="space-between">
                        <Text
                          c="#6C727F"
                          sx={{
                            position: "relative",
                            paddingLeft: 24,
                            "&::before": {
                              content: '""',
                              display: "inline-block",
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: "#9AD9F6",
                              transform: "translateY(-50%)",
                            },
                          }}
                        >
                          {t("INTEREST_SUM")}
                        </Text>
                        <Text c="#121826">
                          {
                            dataDetail?.body.accountOverview
                              ?.interestAmountDisplay
                          }
                        </Text>
                      </Flex>
                      <Divider variant="dashed" />
                      <Flex justify="space-between">
                        <Text fw={600} c="#150904">
                          {t("DEPOSIT_TIME")}
                        </Text>
                        <Text fw={600} c="#150904">
                          {
                            dataDetail?.body.accountOverview
                              ?.numberOfDayProcessDisplay
                          }
                        </Text>
                      </Flex>
                      <Tooltip
                        label={
                          dataDetail?.body.accountOverview?.passedDaysDisplay
                        }
                        styles={{ tooltip: { backgroundColor: "#2A81D0" } }}
                        withArrow
                      >
                        <Progress
                          value={
                            dataDetail?.body.accountOverview?.numberOfDayProcess
                          }
                          radius={4}
                          size={15}
                        />
                      </Tooltip>

                      <Flex justify="space-between" align="center">
                        <Text c="#594B45">{t("TOTAL_TERM")}</Text>
                        <Box
                          style={{
                            border: "1px solid #18181B14",
                            borderRadius: "6px",
                            padding: "4px 8px",
                            width: "fit-content",
                          }}
                        >
                          <Text fz={12} c="#64758F">
                            {beText(
                              dataDetail?.body.accountOverview
                                ?.numberOfDayDisplay,
                            )}
                          </Text>
                        </Box>
                      </Flex>
                    </Stack>
                  </Flex>
                </Center>
              </Tabs.Panel>

              {/* Các tab khác (chưa có dữ liệu) */}
              <Tabs.Panel value="information" pt="md">
                <Flex wrap="wrap" columnGap={40} rowGap={16}>
                  {dataDetail?.body.accountInfoFields
                    ?.filter((item) => item.key !== "Trạng thái")
                    .map((item) => (
                      <Flex justify="space-between" w="44%" key={item.key}>
                        <Text c="#6D6D6D">{beText(item.name)}</Text>
                        <Text fw={500} c="#121826">
                          {beText(item.value)}
                        </Text>
                      </Flex>
                    ))}
                  {dataDetail?.body.accountInfoFields?.some(
                    (item) => item.key === "Trạng thái",
                  ) && (
                    <Flex justify="space-between" w="44%">
                      <Text c="#6D6D6D">{t("STATUS")}</Text>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#E8F7EE",
                        }}
                      >
                        <Image src={ImageTick} height={16} width={16} fit="contain" />
                        <Text size="sm" fw={600} sx={{ color: "#16A34A" }}>
                          {t("ACCOUNT_STATUS_ACTIVE_LABEL")}
                        </Text>
                      </Box>
                    </Flex>
                  )}
                </Flex>
              </Tabs.Panel>
            </Tabs>
          </Card>
          <Space h={24} />
          {/* lịch sử giao dịch */}
          <HistorySaving
            accountName={accountNameFromApi || state?.accountName}
            accountNo={accountNoFromApi || accountNo}
          />
        </Box>
        <Card padding="lg" radius="md" w={540}>
          <Stack>
            <Text fz={20} fw={600} c="#212936">
              {t("INTEREST_DETAILS")}
            </Text>
            {dataDetail?.body.interestRateFields?.map((item) => (
              <Flex justify="space-between" key={item.key}>
                <Text c="#6D6D6D">{beText(item.key)}</Text>
                <Text fw={500} c="#121826">
                  {beText(item.value)}
                </Text>
              </Flex>
            ))}

            <Divider />
            {actionFilter.map((action) => (
              <Flex
                key={action.key}
                style={{ border: "1px solid #EAEAEA", borderRadius: 8 }}
                p={12}
                justify="space-between"
                bg="#F9FAFB"
                onClick={() => {
                  if (action.type === "link") {
                    navigate(action.navigate, {
                      state: { accountNo, type: state?.type },
                    });
                  } else if (action.type === "show") {
                    handleDownloadContract(accountNo);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#F1F5F9",
                    borderColor: "#2A81D0",
                  },
                }}
              >
                <Flex gap={8} align="center">
                  <Box w={32}>
                    <Image src={action.image} />
                  </Box>
                  <Text fw={500}>{action.text}</Text>
                </Flex>
                <IconArrowRight />
              </Flex>
            ))}
          </Stack>
        </Card>
      </Flex>
      <Space h={20} />
      <ExportAccountTransactionHistory
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl={exportFileUrl || ""}
        loading={false}
        title={t("CONTRACT_INFORMATION")}
        onDowload={() => {}}
      />
    </Box>
  );
};
export default TermDepositDetailWrapper;
